import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

const TOUR_OPTIONS = [
  { id: "2026-tura-1", label: "Tura 1" },
  { id: "2026-tura-2", label: "Tura 2" },
  { id: "2026-tura-3", label: "Tura 3" },
  { id: "2026-tura-4", label: "Tura 4" },
];

const DISABLED_FRONT_ROWS = {
  r1: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26],
  r2: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27],
  r2a: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28],
};

const getBasePriceByRow = (rowKey) => {
  const rowNumber = Number(String(rowKey).replace(/[^\d]/g, ""));
  if (rowNumber >= 3 && rowNumber <= 8) return 80;
  return 70;
};

const sortRows = (rows) => {
  return [...rows].sort((a, b) => {
    const [, nA = "", sA = ""] = String(a).match(/^r(\d+)([a-z]*)$/i) || [];
    const [, nB = "", sB = ""] = String(b).match(/^r(\d+)([a-z]*)$/i) || [];
    const diff = parseInt(nA, 10) - parseInt(nB, 10);
    return diff !== 0 ? diff : sA.localeCompare(sB);
  });
};

const sortSeatsDesc = (rowObj = {}) => {
  return Object.values(rowObj).sort((a, b) => Number(b?.seat || 0) - Number(a?.seat || 0));
};

const chunkArray = (arr, size = 30) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const getTourDocId = (tourNumber) => `2026-tura-${tourNumber}`;

const buildSeatKey = (tourId, rowKey, seatNumber) => {
  return `${tourId}_${String(rowKey || "").toLowerCase()}_${Number(seatNumber)}`;
};

export default function GalaAdminSeats() {
  const [selectedTour, setSelectedTour] = useState("2026-tura-1");
  const [galaDoc, setGalaDoc] = useState({ seats: {} });
  const [usersByUid, setUsersByUid] = useState({});
  const [purchasesById, setPurchasesById] = useState({});
  const [selectedSeatDetail, setSelectedSeatDetail] = useState(null);
  const [seatHistory, setSeatHistory] = useState([]);

  const [confirmReset, setConfirmReset] = useState(null);
  const [confirmAccept, setConfirmAccept] = useState(null);
  const [confirmRemoveSeat, setConfirmRemoveSeat] = useState(null);

  const [resetLoading, setResetLoading] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [removeSeatLoading, setRemoveSeatLoading] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "gala", selectedTour), (snap) => {
      if (snap.exists()) {
        setGalaDoc(snap.data() || { seats: {} });
      } else {
        setGalaDoc({ seats: {} });
      }
    });

    return () => unsub();
  }, [selectedTour]);

  const ownerUids = useMemo(() => {
    const ids = [];

    Object.values(galaDoc?.seats || {}).forEach((rowObj) => {
      Object.values(rowObj || {}).forEach((seat) => {
        if (seat?.ownerUid) ids.push(seat.ownerUid);
      });
    });

    return [...new Set(ids.filter(Boolean))];
  }, [galaDoc]);

  const purchaseIds = useMemo(() => {
    const ids = [];

    Object.values(galaDoc?.seats || {}).forEach((rowObj) => {
      Object.values(rowObj || {}).forEach((seat) => {
        if (seat?.purchaseId) ids.push(seat.purchaseId);
      });
    });

    return [...new Set(ids.filter(Boolean))];
  }, [galaDoc]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!ownerUids.length) {
        setUsersByUid({});
        return;
      }

      const chunks = chunkArray(ownerUids, 30);
      const result = {};

      for (const chunk of chunks) {
        const q = query(
          collection(db, "users2026"),
          where(documentId(), "in", chunk)
        );

        const snap = await getDocs(q);

        snap.docs.forEach((d) => {
          const data = d.data() || {};
          result[d.id] = {
            uid: d.id,
            parentName:
              data?.parent?.displayName ||
              data?.displayName ||
              data?.email ||
              d.id,
            email: data?.email || "",
          };
        });
      }

      setUsersByUid(result);
    };

    loadUsers();
  }, [ownerUids]);

  useEffect(() => {
    const loadPurchases = async () => {
      if (!purchaseIds.length) {
        setPurchasesById({});
        return;
      }

      const chunks = chunkArray(purchaseIds, 30);
      const result = {};

      for (const chunk of chunks) {
        const q = query(
          collection(db, "gala-purchases2026"),
          where(documentId(), "in", chunk)
        );

        const snap = await getDocs(q);

        snap.docs.forEach((d) => {
          result[d.id] = {
            id: d.id,
            ...d.data(),
          };
        });
      }

      setPurchasesById(result);
    };

    loadPurchases();
  }, [purchaseIds]);

  useEffect(() => {
    if (!selectedSeatDetail?.seatKey) {
      setSeatHistory([]);
      return;
    }

    const q = query(
      collection(db, "gala-purchases2026"),
      where("seatKeys", "array-contains", selectedSeatDetail.seatKey)
    );

    const unsub = onSnapshot(q, (snap) => {
      const history = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      history.sort((a, b) => {
        const aTime = a?.createdAt?.seconds || 0;
        const bTime = b?.createdAt?.seconds || 0;
        return bTime - aTime;
      });

      setSeatHistory(history);
    });

    return () => unsub();
  }, [selectedSeatDetail?.seatKey]);

  const sortedRows = useMemo(() => {
    return sortRows(Object.keys(galaDoc?.seats || {}));
  }, [galaDoc]);

  const selectedTourLabel =
    TOUR_OPTIONS.find((x) => x.id === selectedTour)?.label || selectedTour;

  const handleResetSeat = async () => {
    if (!confirmReset?.rowKey || !confirmReset?.seatNumber) return;

    try {
      setResetLoading(true);

      const basePrice = getBasePriceByRow(confirmReset.rowKey);

      await setDoc(
        doc(db, "gala", selectedTour),
        {
          seats: {
            [confirmReset.rowKey]: {
              [confirmReset.seatNumber]: {
                seat: Number(confirmReset.seatNumber),
                price: basePrice,
                reduced: false,
                reserved: false,
                status: "available",
                ownerUid: "",
                purchaseId: "",
                finalPrice: "",
              },
            },
          },
        },
        { merge: true }
      );

      if (
        selectedSeatDetail?.rowKey === confirmReset.rowKey &&
        Number(selectedSeatDetail?.seatNumber) === Number(confirmReset.seatNumber)
      ) {
        setSelectedSeatDetail(null);
      }

      setConfirmReset(null);
    } catch (error) {
      console.error("Błąd resetu miejsca:", error);
    } finally {
      setResetLoading(false);
    }
  };

  const handleAcceptOrder = async () => {
    if (!confirmAccept?.purchaseId) return;

    try {
      setAcceptLoading(true);

      const purchase = confirmAccept?.purchase;
      if (!purchase) {
        throw new Error("Nie znaleziono zamówienia.");
      }

      const seats = Array.isArray(purchase?.seats) ? purchase.seats : [];

      for (const seat of seats) {
        const tourDocId = getTourDocId(seat?.tour);
        const row = seat?.row;
        const num = seat?.num;

        if (!tourDocId || !row || num === undefined || num === null) continue;

        await setDoc(
          doc(db, "gala", tourDocId),
          {
            seats: {
              [row]: {
                [num]: {
                  status: "accepted",
                },
              },
            },
          },
          { merge: true }
        );
      }

      await setDoc(
        doc(db, "gala-purchases2026", purchase.id),
        {
          status: "done",
        },
        { merge: true }
      );

      setConfirmAccept(null);
    } catch (error) {
      console.error("Błąd akceptacji zamówienia:", error);
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleRemoveSeatFromOrder = async () => {
    if (!confirmRemoveSeat?.purchaseId || !confirmRemoveSeat?.purchase) return;

    try {
      setRemoveSeatLoading(true);

      const purchase = confirmRemoveSeat.purchase;
      const rowKey = confirmRemoveSeat.rowKey;
      const seatNumber = Number(confirmRemoveSeat.seatNumber);
      const seatKey = confirmRemoveSeat.seatKey;
      const currentTourNumber = Number(String(selectedTour).replace("2026-tura-", ""));

      const currentSeats = Array.isArray(purchase?.seats) ? purchase.seats : [];
      const currentSeatKeys = Array.isArray(purchase?.seatKeys) ? purchase.seatKeys : [];

      const updatedSeats = currentSeats.filter((seat) => {
        return !(
          Number(seat?.tour) === currentTourNumber &&
          String(seat?.row || "").toLowerCase() === String(rowKey || "").toLowerCase() &&
          Number(seat?.num) === seatNumber
        );
      });

      const updatedSeatKeys = currentSeatKeys.filter((k) => k !== seatKey);

      const updatedTotal = updatedSeats.reduce((sum, seat) => {
        return sum + Number(seat?.finalPrice ?? seat?.price ?? 0);
      }, 0);

      const nextStatus = updatedSeats.length > 0 ? (purchase?.status || "pending") : "canceled";

      await setDoc(
        doc(db, "gala", selectedTour),
        {
          seats: {
            [rowKey]: {
              [seatNumber]: {
                seat: seatNumber,
                price: getBasePriceByRow(rowKey),
                reduced: false,
                reserved: false,
                status: "available",
                ownerUid: "",
                purchaseId: "",
                finalPrice: "",
              },
            },
          },
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "gala-purchases2026", purchase.id),
        {
          seats: updatedSeats,
          seatKeys: updatedSeatKeys,
          total: updatedTotal,
          status: nextStatus,
        },
        { merge: true }
      );

      if (
        selectedSeatDetail?.rowKey === rowKey &&
        Number(selectedSeatDetail?.seatNumber) === seatNumber
      ) {
        setSelectedSeatDetail(null);
      }

      setConfirmRemoveSeat(null);
    } catch (error) {
      console.error("Błąd usuwania miejsca z zamówienia:", error);
    } finally {
      setRemoveSeatLoading(false);
    }
  };

  const currentTourNumber = useMemo(() => {
  return Number(String(selectedTour).replace("2026-tura-", ""));
}, [selectedTour]);

const currentTourPurchaseSummary = useMemo(() => {
  const purchases = Object.values(purchasesById || {});

  const activePurchasesForTour = purchases.filter((purchase) => {
    if (!purchase || purchase.status === "canceled") return false;

    const seats = Array.isArray(purchase?.seats) ? purchase.seats : [];
    return seats.some((seat) => Number(seat?.tour) === currentTourNumber);
  });

  const rawReservedSeatsCount = activePurchasesForTour.reduce((sum, purchase) => {
    const seats = Array.isArray(purchase?.seats) ? purchase.seats : [];
    const seatsInCurrentTour = seats.filter(
      (seat) => Number(seat?.tour) === currentTourNumber
    );
    return sum + seatsInCurrentTour.length;
  }, 0);

  const rawUniqueCustomersCount = new Set(
    activePurchasesForTour
      .map((purchase) => purchase?.customer)
      .filter(Boolean)
  ).size;

  const rawRevenue = activePurchasesForTour.reduce((sum, purchase) => {
    const seats = Array.isArray(purchase?.seats) ? purchase.seats : [];
    const seatsInCurrentTour = seats.filter(
      (seat) => Number(seat?.tour) === currentTourNumber
    );

    const seatsRevenue = seatsInCurrentTour.reduce((seatSum, seat) => {
      return seatSum + Number(seat?.finalPrice ?? seat?.price ?? 0);
    }, 0);

    return sum + seatsRevenue;
  }, 0);

const reservedSeatsCount = Math.max(0, rawReservedSeatsCount - 16);
const uniqueCustomersCount = Math.max(0, rawUniqueCustomersCount - 1);

// 8 biletów po 70 zł i 8 biletów po 80 zł
const adminSeatsValue = (8 * 70) + (8 * 80);
const totalRevenue = Math.max(0, rawRevenue - adminSeatsValue);

const avgSeatsPerCustomer =
  uniqueCustomersCount > 0
    ? (reservedSeatsCount / uniqueCustomersCount).toFixed(2)
    : "0.00";

const avgTicketPrice =
  reservedSeatsCount > 0
    ? (totalRevenue / reservedSeatsCount).toFixed(2)
    : "0.00";

  return {
    reservedSeatsCount,
    uniqueCustomersCount,
    avgSeatsPerCustomer,
    avgTicketPrice,
    totalRevenue,
  };
}, [purchasesById, currentTourNumber]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        backgroundColor: "rgb(240,240,240)",
        width: "100%",
        minHeight: "100vh",
        gap: "25px",
        paddingBottom: "50px",
      }}
    >
      <nav
        style={{
          width: "100%",
          borderBottom: "1px solid rgb(30,30,30)",
          minHeight: "70px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgb(240,240,240)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "92%",
            gap: "20px",
            flexWrap: "wrap",
            padding: "10px 0",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              color: "rgb(30,30,30)",
              fontWeight: "bold",
              margin: 0,
            }}
          >
            Admin – mapa miejsc gali
          </h3>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {TOUR_OPTIONS.map((tour) => (
              <button
                key={tour.id}
                onClick={() => {
                  setSelectedTour(tour.id);
                  setSelectedSeatDetail(null);
                  setSeatHistory([]);
                }}
                style={{
                  minWidth: "90px",
                  height: "36px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: selectedTour === tour.id ? "white" : "rgb(35,35,35)",
                  backgroundColor: selectedTour === tour.id ? "#0045C7" : "white",
                  borderRadius: "5px",
                  border:
                    selectedTour === tour.id
                      ? "2px solid #0045C7"
                      : "1px solid rgb(180,180,180)",
                  cursor: "pointer",
                  padding: "0 12px",
                }}
              >
                {tour.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

<div
  style={{
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    width: "100%",
  }}
>
  <h2
    style={{
      color: "rgb(35,35,35)",
      fontSize: "22px",
      fontWeight: "bold",
      margin: 0,
    }}
  >
    {selectedTourLabel}
  </h2>

  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      gap: "18px",
      padding: "12px 16px",
      border: "1px solid rgb(30,30,30,0.12)",
      borderRadius: "8px",
      backgroundColor: "rgb(248,248,248)",
      width: "92%",
      maxWidth: "900px",
    }}
  >
    <h3 style={{ fontSize: "13px", color: "rgb(30,30,30)", margin: 0 }}>
      Ilość miejsc zarezerwowanych: <b>{currentTourPurchaseSummary.reservedSeatsCount}</b>
    </h3>

    <h3 style={{ fontSize: "13px", color: "rgb(30,30,30)", margin: 0 }}>
      Ilość osób, które zamówiły: <b>{currentTourPurchaseSummary.uniqueCustomersCount}</b>
    </h3>

    <h3 style={{ fontSize: "13px", color: "rgb(30,30,30)", margin: 0 }}>
      Średnia miejsc na osobę: <b>{currentTourPurchaseSummary.avgSeatsPerCustomer}</b>
    </h3>

    <h3 style={{ fontSize: "13px", color: "rgb(30,30,30)", margin: 0 }}>
      Średnia cena za bilet: <b>{currentTourPurchaseSummary.avgTicketPrice} zł</b>
    </h3>

    <h3 style={{ fontSize: "13px", color: "rgb(30,30,30)", margin: 0 }}>
      Suma za sprzedane bilety: <b>{currentTourPurchaseSummary.totalRevenue} zł</b>
    </h3>
  </div>

  <div
    style={{
      width: "75%",
      maxWidth: "700px",
      minWidth: "280px",
      height: "50px",
      borderRadius: "5px",
      border: "2px solid rgb(60,60,60)",
      borderTop: "0px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: "10px",
    }}
  >
    <h3
      style={{
        color: "rgb(30,30,30)",
        fontSize: "22px",
        textAlign: "center",
        fontWeight: "bold",
        margin: 0,
      }}
    >
      SCENA
    </h3>
  </div>
</div>

      <div
        style={{
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
          width: "100%",
          paddingBottom: "15px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            minWidth: "700px",
            marginLeft: "70px",
            marginRight: "20px",
            paddingLeft: "20px",
            boxSizing: "border-box",
          }}
        >
          <h3
            style={{
              color: "rgb(50,50,50)",
              fontSize: "13px",
              textAlign: "center",
              fontWeight: "bold",
              letterSpacing: "0.1px",
              marginBottom: "5px",
            }}
          >
            MIEJSCA WYŁĄCZONE Z UŻYTKU
          </h3>

          {["r1", "r2", "r2a"].map((r) => (
            <div
              key={r}
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: "4px",
                marginBottom: r === "r2a" ? "20px" : "0",
              }}
            >
              <h3
                style={{
                  color: "rgb(35,35,35)",
                  fontSize: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  letterSpacing: "0.1px",
                  padding: "0px 3px",
                  minWidth: "25px",
                }}
              >
                {r.toUpperCase()}
              </h3>

              {DISABLED_FRONT_ROWS[r]?.sort((a, b) => b - a)?.map((s) => (
                <div
                  key={s}
                  style={{
                    width: "21px",
                    height: "21px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "1px solid #46464f",
                    backgroundColor: "#46464f",
                    cursor: "not-allowed",
                    borderRadius: "2px",
                  }}
                >
                  <h3
                    style={{
                      color: "rgb(230,230,230)",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "10px",
                      margin: 0,
                    }}
                  >
                    {s}
                  </h3>
                </div>
              ))}

              <h3
                style={{
                  color: "rgb(35,35,35)",
                  fontSize: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  letterSpacing: "0.1px",
                  padding: "0px 3px",
                  minWidth: "25px",
                }}
              >
                {r.toUpperCase()}
              </h3>
            </div>
          ))}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {sortedRows.map((rowKey) => (
              <div
                key={rowKey}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <h3
                  style={{
                    color: "rgb(35,35,35)",
                    fontSize: "12px",
                    textAlign: "center",
                    fontWeight: "bold",
                    letterSpacing: "0.1px",
                    padding: "0px 3px",
                    minWidth: "25px",
                  }}
                >
                  {rowKey.toUpperCase()}
                </h3>

                {sortSeatsDesc(galaDoc?.seats?.[rowKey] || {}).map((seatData) => {
                  const seatNumber = seatData?.seat;
                  const ownerUid = seatData?.ownerUid || "";
                  const ownerName = ownerUid ? usersByUid?.[ownerUid]?.parentName || ownerUid : "";
                  const isReserved =
                    seatData?.reserved === true || seatData?.status !== "available";
                  const isSelected =
                    selectedSeatDetail?.rowKey === rowKey &&
                    Number(selectedSeatDetail?.seatNumber) === Number(seatNumber);

                  return (
                    <div
                      key={`${rowKey}_${seatNumber}`}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "4px",
                        minHeight: "21px",
                      }}
                    >
                      <div
                        onClick={() =>
                          setSelectedSeatDetail({
                            rowKey,
                            seatNumber,
                            seatKey: buildSeatKey(selectedTour, rowKey, seatNumber),
                            seatData,
                            ownerName,
                            selectedTourLabel,
                          })
                        }
                        style={{
                          width: "21px",
                          height: "21px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          border: isSelected
                            ? "1px solid #0045C7"
                            : seatData?.status === "accepted"
                            ? "1px solid #2a7325"
                            : isReserved
                            ? "1px solid #46464f"
                            : "1px solid rgb(60,60,60)",
                          backgroundColor: isSelected
                            ? "#0045C7"
                            : seatData?.status === "accepted"
                            ? "#2a7325"
                            : isReserved
                            ? "#46464f"
                            : "white",
                          borderRadius: "2px",
                          cursor: "pointer",
                          transition: "0.2s",
                        }}
                      >
                        <h3
                          style={{
                            color: isSelected || isReserved ? "rgb(230,230,230)" : "rgb(35,35,35)",
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: "10px",
                            margin: 0,
                          }}
                        >
                          {seatNumber}
                        </h3>
                      </div>
                    </div>
                  );
                })}

                <h3
                  style={{
                    color: "rgb(35,35,35)",
                    fontSize: "12px",
                    textAlign: "center",
                    fontWeight: "bold",
                    letterSpacing: "0.1px",
                    padding: "0px 3px",
                    minWidth: "25px",
                  }}
                >
                  {rowKey.toUpperCase()}
                </h3>
              </div>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: "20px",
              marginTop: "15px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "2px",
                  border: "1px solid rgb(60,60,60)",
                }}
              />
              <h3
                style={{
                  color: "rgb(35,35,35)",
                  fontSize: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  letterSpacing: "0.1px",
                  padding: "0px 3px",
                  marginTop: "1px",
                }}
              >
                - Miejsce wolne
              </h3>
            </div>

            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "2px",
                  border: "1px solid #46464f",
                  backgroundColor: "#46464f",
                }}
              />
              <h3
                style={{
                  color: "rgb(35,35,35)",
                  fontSize: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  letterSpacing: "0.1px",
                  padding: "0px 3px",
                  marginTop: "1px",
                }}
              >
                - Miejsce zajęte
              </h3>
            </div>

            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "2px",
                  border: "1px solid #0045C7",
                  backgroundColor: "#0045C7",
                }}
              />
              <h3
                style={{
                  color: "rgb(35,35,35)",
                  fontSize: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  letterSpacing: "0.1px",
                  padding: "0px 3px",
                  marginTop: "1px",
                }}
              >
                - Miejsce zaznaczone
              </h3>
            </div>
          </div>
        </div>
      </div>

      {selectedSeatDetail && (
        <div
          style={{
            width: "92%",
            maxWidth: "1100px",
            display: "flex",
            flexDirection: "column",
            gap: "18px",
            backgroundColor: "rgba(255,255,255,0.82)",
            borderRadius: "10px",
            padding: "22px",
            boxShadow:
              "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              color: "rgb(30,30,30)",
              fontWeight: "bold",
              margin: 0,
            }}
          >
            Szczegóły klikniętego miejsca
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Tura</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                {selectedSeatDetail?.selectedTourLabel}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Rząd</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                {selectedSeatDetail?.rowKey?.toUpperCase()}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Miejsce</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                {selectedSeatDetail?.seatNumber}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Status</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                {selectedSeatDetail?.seatData?.status || "available"}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Cena podstawowa</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                {selectedSeatDetail?.seatData?.price ?? getBasePriceByRow(selectedSeatDetail?.rowKey)} zł
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Cena końcowa</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                {selectedSeatDetail?.seatData?.finalPrice !== "" &&
                selectedSeatDetail?.seatData?.finalPrice !== undefined
                  ? `${selectedSeatDetail.seatData.finalPrice} zł`
                  : "-"}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Ulga</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                {selectedSeatDetail?.seatData?.reduced ? "TAK" : "NIE"}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Reserved</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                {selectedSeatDetail?.seatData?.reserved ? "TAK" : "NIE"}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Owner UID</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0", wordBreak: "break-word" }}>
                {selectedSeatDetail?.seatData?.ownerUid || "-"}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Posiadacz</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0", wordBreak: "break-word" }}>
                {selectedSeatDetail?.ownerName || "-"}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Purchase ID</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0", wordBreak: "break-word" }}>
                {selectedSeatDetail?.seatData?.purchaseId || "-"}
              </h3>
            </div>

            <div>
              <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Seat key</h3>
              <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0", wordBreak: "break-word" }}>
                {selectedSeatDetail?.seatKey || "-"}
              </h3>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "10px",
            }}
          >
            {(selectedSeatDetail?.seatData?.reserved === true ||
              selectedSeatDetail?.seatData?.status !== "available") && (
              <button
                onClick={() =>
                  setConfirmReset({
                    rowKey: selectedSeatDetail?.rowKey,
                    seatNumber: selectedSeatDetail?.seatNumber,
                    ownerName: selectedSeatDetail?.ownerName,
                  })
                }
                style={{
                  minWidth: "120px",
                  height: "36px",
                  fontSize: "12px",
                  borderRadius: "5px",
                  border: "1px solid #DC143C",
                  backgroundColor: "#DC143C",
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Wyzeruj miejsce
              </button>
            )}

            {selectedSeatDetail?.seatData?.purchaseId &&
              selectedSeatDetail?.seatData?.status === "pending" &&
              purchasesById?.[selectedSeatDetail?.seatData?.purchaseId] && (
                <button
                  onClick={() =>
                    setConfirmAccept({
                      purchaseId: selectedSeatDetail?.seatData?.purchaseId,
                      ownerName: selectedSeatDetail?.ownerName,
                      rowKey: selectedSeatDetail?.rowKey,
                      seatNumber: selectedSeatDetail?.seatNumber,
                      purchase: purchasesById?.[selectedSeatDetail?.seatData?.purchaseId],
                    })
                  }
                  style={{
                    minWidth: "140px",
                    height: "36px",
                    fontSize: "12px",
                    borderRadius: "5px",
                    border: "1px solid #12B552",
                    backgroundColor: "#12B552",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  Akceptuj zamówienie
                </button>
              )}

            {selectedSeatDetail?.seatData?.purchaseId && (
              <button
                onClick={() =>
                  setConfirmRemoveSeat({
                    purchaseId: selectedSeatDetail?.seatData?.purchaseId,
                    rowKey: selectedSeatDetail?.rowKey,
                    seatNumber: selectedSeatDetail?.seatNumber,
                    ownerName: selectedSeatDetail?.ownerName,
                    seatData: selectedSeatDetail?.seatData,
                    seatKey: selectedSeatDetail?.seatKey,
                    purchase:
                      seatHistory.find(
                        (x) => x.id === selectedSeatDetail?.seatData?.purchaseId
                      ) || purchasesById?.[selectedSeatDetail?.seatData?.purchaseId],
                  })
                }
                style={{
                  minWidth: "170px",
                  height: "36px",
                  fontSize: "12px",
                  borderRadius: "5px",
                  border: "1px solid #b36b00",
                  backgroundColor: "#b36b00",
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Usuń miejsce z zamówienia
              </button>
            )}
          </div>

          <div
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "rgba(0,0,0,0.08)",
            }}
          />

          <h3
            style={{
              fontSize: "18px",
              color: "rgb(30,30,30)",
              fontWeight: "bold",
              margin: 0,
            }}
          >
            Historia zamówień dla tego miejsca
          </h3>

          {seatHistory.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "14px",
              }}
            >
              {seatHistory.map((purchase) => (
                <div
                  key={purchase.id}
                  style={{
                    borderRadius: "8px",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    padding: "14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>ID zamówienia</h3>
                      <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0", wordBreak: "break-word" }}>
                        {purchase?.id || "-"}
                      </h3>
                    </div>

                    <div>
                      <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Status zamówienia</h3>
                      <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                        {purchase?.status || "-"}
                      </h3>
                    </div>

                    <div>
                      <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Customer</h3>
                      <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0", wordBreak: "break-word" }}>
                        {purchase?.customer || "-"}
                      </h3>
                    </div>

                    <div>
                      <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Customer email</h3>
                      <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0", wordBreak: "break-word" }}>
                        {purchase?.customerEmail || "-"}
                      </h3>
                    </div>

                    <div>
                      <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Suma</h3>
                      <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                        {purchase?.total ?? "-"} zł
                      </h3>
                    </div>

                    <div>
                      <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Liczba miejsc</h3>
                      <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                        {Array.isArray(purchase?.seats) ? purchase.seats.length : 0}
                      </h3>
                    </div>

                    <div>
                      <h3 style={{ fontSize: "12px", color: "rgb(120,120,120)", margin: 0 }}>Data zamówienia</h3>
                      <h3 style={{ fontSize: "14px", color: "rgb(35,35,35)", margin: "4px 0 0 0" }}>
                        {purchase?.createdAt?.seconds
                          ? new Date(purchase.createdAt.seconds * 1000).toLocaleString("pl-PL")
                          : "-"}
                      </h3>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "14px",
                        color: "rgb(30,30,30)",
                        fontWeight: "bold",
                        margin: 0,
                      }}
                    >
                      Miejsca z zamówienia
                    </h3>

                    {Array.isArray(purchase?.seats) && purchase.seats.length > 0 ? (
                      purchase.seats.map((seat, index) => {
                        const isCurrentSeat =
                          Number(seat?.tour) === Number(String(selectedTour).replace("2026-tura-", "")) &&
                          String(seat?.row || "").toLowerCase() === String(selectedSeatDetail?.rowKey || "").toLowerCase() &&
                          Number(seat?.num) === Number(selectedSeatDetail?.seatNumber);

                        return (
                          <div
                            key={`${purchase.id}_${index}`}
                            style={{
                              padding: "10px 12px",
                              borderRadius: "6px",
                              backgroundColor: isCurrentSeat ? "rgba(0,69,199,0.08)" : "rgba(255,255,255,0.8)",
                              border: isCurrentSeat ? "1px solid rgba(0,69,199,0.22)" : "1px solid rgba(0,0,0,0.05)",
                              fontSize: "13px",
                              color: "rgb(45,45,45)",
                            }}
                          >
                            Tura {seat?.tour} | Rząd: {String(seat?.row || "").toUpperCase()} | Miejsce: {seat?.num} | Cena: {seat?.finalPrice ?? seat?.price ?? "-"} zł | Ulga: {seat?.isDiscount ? "TAK" : "NIE"}
                          </div>
                        );
                      })
                    ) : (
                      <h3
                        style={{
                          fontSize: "13px",
                          color: "rgb(120,120,120)",
                          fontWeight: "normal",
                          margin: 0,
                        }}
                      >
                        Brak miejsc w zamówieniu.
                      </h3>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <h3
              style={{
                fontSize: "14px",
                color: "rgb(120,120,120)",
                fontWeight: "normal",
                margin: 0,
              }}
            >
              Brak historii zamówień dla tego miejsca.
            </h3>
          )}
        </div>
      )}

      {confirmReset && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100,
          }}
        >
          <div
            style={{
              width: "92%",
              maxWidth: "520px",
              backgroundColor: "white",
              borderRadius: "10px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "20px",
                color: "rgb(30,30,30)",
              }}
            >
              Wyzerować miejsce?
            </h3>

            <div
              style={{
                fontSize: "14px",
                color: "rgb(50,50,50)",
                lineHeight: 1.5,
              }}
            >
              <div><b>Tura:</b> {selectedTourLabel}</div>
              <div><b>Rząd:</b> {confirmReset?.rowKey?.toUpperCase()}</div>
              <div><b>Miejsce:</b> {confirmReset?.seatNumber}</div>
              <div><b>Posiadacz:</b> {confirmReset?.ownerName || "brak"}</div>
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "rgb(110,110,110)",
                lineHeight: 1.5,
              }}
            >
              Reset ustawi miejsce jak nowe:
              <br />
              `status: "available"`, `reserved: false`, `reduced: false`,
              wyczyści `ownerUid`, `purchaseId`, `finalPrice`
              i przywróci bazową cenę wg rzędu.
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "10px",
                marginTop: "8px",
              }}
            >
              <button
                onClick={() => setConfirmReset(null)}
                style={{
                  fontSize: "12px",
                  color: "rgb(30,30,30)",
                  borderRadius: "5px",
                  backgroundColor: "#fff",
                  border: "1px solid rgb(30,30,30,0.2)",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: "pointer",
                }}
              >
                Anuluj
              </button>

              <button
                onClick={handleResetSeat}
                disabled={resetLoading}
                style={{
                  fontSize: "12px",
                  color: "white",
                  borderRadius: "5px",
                  backgroundColor: "#DC143C",
                  border: "2px solid #DC143C",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: resetLoading ? "default" : "pointer",
                  opacity: resetLoading ? 0.7 : 1,
                }}
              >
                {resetLoading ? "Resetowanie..." : "Potwierdź reset"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAccept && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100,
          }}
        >
          <div
            style={{
              width: "92%",
              maxWidth: "520px",
              backgroundColor: "white",
              borderRadius: "10px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "20px",
                color: "rgb(30,30,30)",
              }}
            >
              Akceptować zamówienie?
            </h3>

            <div
              style={{
                fontSize: "14px",
                color: "rgb(50,50,50)",
                lineHeight: 1.5,
              }}
            >
              <div><b>Tura:</b> {selectedTourLabel}</div>
              <div><b>Rząd:</b> {confirmAccept?.rowKey?.toUpperCase()}</div>
              <div><b>Miejsce:</b> {confirmAccept?.seatNumber}</div>
              <div><b>Posiadacz:</b> {confirmAccept?.ownerName || "brak"}</div>
              <div><b>ID zamówienia:</b> {confirmAccept?.purchaseId}</div>
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "rgb(110,110,110)",
                lineHeight: 1.5,
              }}
            >
              Ta operacja zaakceptuje wszystkie miejsca powiązane z tym zamówieniem.
              <br />
              Miejsca dostaną `status: "accepted"`, a zamówienie `status: "done"`.
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "10px",
                marginTop: "8px",
              }}
            >
              <button
                onClick={() => setConfirmAccept(null)}
                style={{
                  fontSize: "12px",
                  color: "rgb(30,30,30)",
                  borderRadius: "5px",
                  backgroundColor: "#fff",
                  border: "1px solid rgb(30,30,30,0.2)",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: "pointer",
                }}
              >
                Anuluj
              </button>

              <button
                onClick={handleAcceptOrder}
                disabled={acceptLoading}
                style={{
                  fontSize: "12px",
                  color: "white",
                  borderRadius: "5px",
                  backgroundColor: "#12B552",
                  border: "2px solid #12B552",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: acceptLoading ? "default" : "pointer",
                  opacity: acceptLoading ? 0.7 : 1,
                }}
              >
                {acceptLoading ? "Akceptowanie..." : "Potwierdź akceptację"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmRemoveSeat && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100,
          }}
        >
          <div
            style={{
              width: "92%",
              maxWidth: "520px",
              backgroundColor: "white",
              borderRadius: "10px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "20px",
                color: "rgb(30,30,30)",
              }}
            >
              Usunąć miejsce z zamówienia?
            </h3>

            <div
              style={{
                fontSize: "14px",
                color: "rgb(50,50,50)",
                lineHeight: 1.5,
              }}
            >
              <div><b>ID zamówienia:</b> {confirmRemoveSeat?.purchaseId}</div>
              <div><b>Rząd:</b> {confirmRemoveSeat?.rowKey?.toUpperCase()}</div>
              <div><b>Miejsce:</b> {confirmRemoveSeat?.seatNumber}</div>
              <div><b>Posiadacz:</b> {confirmRemoveSeat?.ownerName || "brak"}</div>
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "rgb(110,110,110)",
                lineHeight: 1.5,
              }}
            >
              To miejsce zostanie usunięte z zamówienia i wróci do puli rezerwacji.
              Jeśli było to ostatnie miejsce w zamówieniu, zamówienie zostanie oznaczone jako anulowane.
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "10px",
                marginTop: "8px",
              }}
            >
              <button
                onClick={() => setConfirmRemoveSeat(null)}
                style={{
                  fontSize: "12px",
                  color: "rgb(30,30,30)",
                  borderRadius: "5px",
                  backgroundColor: "#fff",
                  border: "1px solid rgb(30,30,30,0.2)",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: "pointer",
                }}
              >
                Anuluj
              </button>

              <button
                onClick={handleRemoveSeatFromOrder}
                disabled={removeSeatLoading}
                style={{
                  fontSize: "12px",
                  color: "white",
                  borderRadius: "5px",
                  backgroundColor: "#b36b00",
                  border: "2px solid #b36b00",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: removeSeatLoading ? "default" : "pointer",
                  opacity: removeSeatLoading ? 0.7 : 1,
                }}
              >
                {removeSeatLoading ? "Usuwanie..." : "Potwierdź usunięcie"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}